// src/pages/Comercial/Produtos/ProdutoData.ts
import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type Produto = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;

  /** NOVOS CAMPOS */
  quantidade: number | null;
  custo_compra: number | null;
  margem_percentual: number | null;
  preco_venda: number | null;

  /** Moeda do produto (BRL, USD, etc.) */
  moeda: string | null;

  /** Mantido para retrocompatibilidade com telas antigas */
  custo_unitario: number | string | null;
};

// 1) --- Tipos: inclua as variantes que a API comercial retorna ---
type ProdutoRaw = {
  codigo?: string;
  ncm?: string;
  nome?: string;
  classificacao?: string;
  posicao?: string;

  // possíveis nomes (suprimentos e comercial)
  quantidade?: number | string | null;
  qtd?: number | string | null;
  quantidade_atual?: number | string | null;
  estoque_atual?: { quantidade_atual?: number | string | null } | null;

  custo_compra?: number | string | null;
  custo_unitario?: number | string | null;
  custo?: number | string | null;
  custo_unitario_atual?: number | string | null;

  margem_percentual?: number | string | null;
  margem?: number | string | null;

  preco_venda?: number | string | null;
  preco?: number | string | null;

  /** Moeda vinda da API, ex: "BRL" ou "USD" */
  moeda?: string | null;

  ocultar?: boolean;
  vendavel?: boolean;
  data_definicao?: string | null;
};

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: Produto[]; // ← já virá só com visíveis (normalizados)
};

type AwaitResult = { changed: boolean; versao: number | null };

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/comercial";
const URL = `${BASE}/produtos-comercial`;
const URL_AWAIT =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/produtos/await";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
// Segure próximo do hold do servidor p/ reduzir chamadas e evitar sobreposição
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120; // pequena folga entre awaits

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function produtosMudaram(a: Produto[], b: Produto[]): boolean {
  if (a.length !== b.length) return true;
  return JSON.stringify(a) !== JSON.stringify(b);
}

function toNumberSafe(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;

  // Heurística pt-BR vs en-US
  if (/[.,]/.test(s)) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      // vírgula decimal — 14.276,28 -> 14276.28
      const n = Number(s.replace(/\./g, "").replace(",", "."));
      return Number.isFinite(n) ? n : null;
    }
    if (lastDot > lastComma) {
      // ponto decimal — 14,276.28 -> 14276.28
      const n = Number(s.replace(/,/g, ""));
      return Number.isFinite(n) ? n : null;
    }
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Normaliza um item bruto do backend para o shape Produto */
// 2) --- Normalização: dê prioridade aos nomes da API comercial ---
function normalizeProduto(raw: ProdutoRaw): Produto {
  const quantidadeRaw =
    raw.quantidade_atual ?? // 1ª prioridade (comercial)
    raw.quantidade ??
    raw.qtd ??
    raw.estoque_atual?.quantidade_atual ??
    null;

  const custoCompraRaw =
    raw.custo_unitario_atual ?? // 1ª prioridade (comercial)
    raw.custo_compra ??
    raw.custo_unitario ??
    raw.custo ??
    null;

  const margemRaw = raw.margem_percentual ?? raw.margem ?? null;
  const precoVendaRaw = raw.preco_venda ?? raw.preco ?? null;

  const custoCompra = toNumberSafe(custoCompraRaw);
  const margem_percentual = toNumberSafe(margemRaw);
  const preco_venda = toNumberSafe(precoVendaRaw);
  const quantidade = toNumberSafe(quantidadeRaw);

  // Se não vier nada, assume BRL por padrão
  const moeda = (raw.moeda ?? "BRL").toUpperCase();

  return {
    codigo: String(raw.codigo ?? ""),
    ncm: String(raw.ncm ?? ""),
    nome: String(raw.nome ?? ""),
    classificacao: String(raw.classificacao ?? ""),
    posicao: String(raw.posicao ?? ""),

    quantidade,
    custo_compra: custoCompra,
    margem_percentual,
    preco_venda,
    moeda,

    // retrocompat
    custo_unitario:
      raw.custo_unitario != null
        ? raw.custo_unitario
        : raw.custo_compra ?? raw.custo_unitario_atual ?? null,
  };
}

/** ---------------------- HTTP helpers ---------------------- */
async function headVersao(signal?: AbortSignal): Promise<number | null> {
  try {
    const res = await fetch(URL, {
      method: "HEAD",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;
    return Number.isFinite(n!) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  _versaoAtual: number | null,
  _signal?: AbortSignal
): Promise<ListaResp> {
  const headers: Record<string, string> = { Accept: "application/json" };

  const res = await fetch(URL, {
    method: "GET",
    headers,
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;
  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = await res.json();
  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr!) ? verHdr : verBody;

  // Preferir 'visiveis' quando o backend já splitou,
  // senão, filtra 'data' removendo { ocultar: true }.
  const visiveisRaw: ProdutoRaw[] | undefined = Array.isArray(json?.visiveis)
    ? (json.visiveis as ProdutoRaw[])
    : undefined;

  const dataRaw: ProdutoRaw[] =
    visiveisRaw ?? (json?.data as ProdutoRaw[]) ?? [];

  // Se não veio 'visiveis', faz o filtro local; se a flag não existir, mantém.
  const onlyVisible: ProdutoRaw[] =
    visiveisRaw != null
      ? visiveisRaw
      : dataRaw.filter((p) => p?.ocultar !== true);

  // Normaliza cada item para o shape Produto com os campos novos
  const cleanVisible: Produto[] = onlyVisible.map((r) => normalizeProduto(r));

  return {
    fonte,
    versao: versao ?? null,
    data: cleanVisible,
  };
}

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${since}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useProdutosDisponiveis() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, _setVersao] = useState<number | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false); // TRUE enquanto GET /produtos está ativo
  const awaitCtrlRef = useRef<AbortController | null>(null); // controller do /await atual
  const awaitSupported = useRef<boolean | null>(null); // auto-detecção /await
  const awaitInFlightRef = useRef(false); // single-flight para /await

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    _setVersao(v);
  };

  async function carregarLista(cur: number | null) {
    fetchingRef.current = true;
    try {
      const lista = await getLista(cur /* sem signal */);
      if (lista.versao != null) setVersao(lista.versao);
      setProdutos((prev) =>
        produtosMudaram(prev, lista.data) ? lista.data : prev
      );
      setFonte(lista.fonte);
    } finally {
      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    (async () => {
      try {
        const vHead = await headVersao(); // opcional
        await carregarLista(vHead ?? null);
        setLoading(false);

        while (!stop.current) {
          // Evita GET simultâneo
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }
          if (stop.current) break;

          const cur = versaoRef.current;

          // ---------- Long-poll preferencial ----------
          if (awaitSupported.current !== false && cur != null) {
            // Garante single-flight: se já tem /await pendente, aguarde
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const acAwait = new AbortController();
            awaitCtrlRef.current = acAwait;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, acAwait.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                // erro transitório
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              // limpa controles
              if (awaitCtrlRef.current === acAwait) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              // provavelmente não suportado (CORS / 404 / etc.)
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }
              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            const avancou = (resp.versao ?? -1) > cur;
            if (resp.changed || avancou) {
              await carregarLista(cur);
              await sleep(20);
            } else {
              // sem mudanças → pequena folga antes do próximo long-poll
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          // --------- Fallback por HEAD/ETag ---------
          const vNow = await headVersao();
          const cur2 = versaoRef.current;

          if (vNow !== null && cur2 !== null && vNow === cur2) {
            await sleep(POLL_FALLBACK_MS);
            continue;
          }
          if (vNow === null && cur2 !== null) {
            await sleep(POLL_FALLBACK_MS);
          }

          await carregarLista(cur2);
        }
      } catch (e) {
        if (!stop.current)
          console.error("useProdutosDisponiveis loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetchingRef.current) await sleep(25);
    await carregarLista(versaoRef.current);
  };

  return { produtos, loading, refresh, fonte, versao };
}
